import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchCapitalGains, fetchCapitalGainsLongPeriod, fetchCapitalGainsShortPeriod } from '../store/capitalGainSlice';
import './CapitalGain.css';
import { fetchClients } from '../store/clientsSlice';

const formatAmount = (value) => Number(value ?? 0).toLocaleString('en-IN', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const formatDate = (value) => {
  if (!value) {
    return '-';
  }
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'Asia/Kolkata',
  }).format(new Date(value));
};

const CapitalGain = () => {
  const dispatch = useDispatch();
  const { clientId, financialYear } = useSelector((state) => state.app);
  const {
    ltcg,
    ltcgTotal,
    ltcgTaxableTotal,
    ltcgThresholdRelief,
    stcg,
    stcgTotal,
    stcgTaxableTotal,
    stcgPeriod,
    ltcgPeriod,
    loading,
    error,
  } = useSelector((state) => state.capitalGains);

  useEffect(() => {
    dispatch(fetchClients()); // Ensure clients are loaded
    dispatch(fetchCapitalGains());
    dispatch(fetchCapitalGainsShortPeriod());
    dispatch(fetchCapitalGainsLongPeriod());
  }, [dispatch,clientId,financialYear]);

  return (
    <div className="capital-gain-page">
      <h2 className="capital-gain-title">Capital Gain Summary</h2>
      <p className="financial-page-subtitle">Keep capital gain schedules on the same design system as tax computation and other reporting pages.</p>
      {loading && <p className="loading-message">Loading...</p>}
      {error && <p className="error-message">{error}</p>}

      <div className="capital-gain-section">
        <h3>Long-Term Capital Gain (LTCG)</h3>
        {ltcg && ltcg.length > 0 ? (
          <table className="capital-gain-table">
            <thead>
              <tr>
                <th>Asset</th>
                <th>Class</th>
                <th>Section</th>
                <th>Acquired</th>
                <th>Transferred</th>
                <th>Holding Days</th>
                <th>Gross Consideration</th>
                <th>Transfer Expenses</th>
                <th>Actual Cost</th>
                <th>31-Jan-2018 FMV</th>
                <th>Deemed Cost</th>
                <th>Cost of Improvement</th>
                <th>Gain</th>
                <th>Rate %</th>
              </tr>
            </thead>
            <tbody>
              {ltcg.map((gain, index) => (
                <tr key={index}>
                  <td>{gain.assetName}</td>
                  <td>{gain.assetClass}</td>
                  <td>{gain.section}</td>
                  <td>{formatDate(gain.acquisitionDate)}</td>
                  <td>{formatDate(gain.transferDate)}</td>
                  <td>{gain.holdingPeriodDays}</td>
                  <td>₹ {formatAmount(gain.grossConsideration)}</td>
                  <td>₹ {formatAmount(gain.expenseRelatedToTransferExceptSTT)}</td>
                  <td>₹ {formatAmount(gain.actualCostOfAcquisition)}</td>
                  <td>{gain.grandfatheredFairMarketValue != null ? `₹ ${formatAmount(gain.grandfatheredFairMarketValue)}` : '-'}</td>
                  <td>₹ {formatAmount(gain.costOfAcqusition)}</td>
                  <td>{gain.costOfImprovement != null ? `₹ ${formatAmount(gain.costOfImprovement)}` : 'N/A'}</td>
                  <td>₹ {formatAmount(gain.gainAmount)}</td>
                  <td>{formatAmount(gain.applicableTaxRate)}</td>
                </tr>
              ))}
              <tr className="total-row">
                <td colSpan="11">Gross LTCG</td>
                <td>₹ {ltcgTotal !== null ? formatAmount(ltcgTotal) : 'N/A'}</td>
                <td>-</td>
              </tr>
              <tr className="total-row">
                <td colSpan="11">112A Threshold Relief</td>
                <td>₹ {ltcgThresholdRelief !== null ? formatAmount(ltcgThresholdRelief) : '0.00'}</td>
                <td>-</td>
              </tr>
              <tr className="total-row">
                <td colSpan="11">Taxable LTCG</td>
                <td>₹ {ltcgTaxableTotal !== null ? formatAmount(ltcgTaxableTotal) : 'N/A'}</td>
                <td>-</td>
              </tr>
            </tbody>
          </table>
        ) : (
          <p>No Long-Term Capital Gain data available.</p>
        )}
      </div>

      <div className="capital-gain-section">
        <h3>Short-Term Capital Gain (STCG)</h3>
        {stcg && stcg.length > 0 ? (
          <table className="capital-gain-table">
            <thead>
              <tr>
                <th>Asset</th>
                <th>Class</th>
                <th>Section</th>
                <th>Acquired</th>
                <th>Transferred</th>
                <th>Holding Days</th>
                <th>Gross Consideration</th>
                <th>Transfer Expenses</th>
                <th>Actual Cost</th>
                <th>31-Jan-2018 FMV</th>
                <th>Deemed Cost</th>
                <th>Cost of Improvement</th>
                <th>Gain</th>
                <th>Rate %</th>
              </tr>
            </thead>
            <tbody>
              {stcg.map((gain, index) => (
                <tr key={index}>
                  <td>{gain.assetName}</td>
                  <td>{gain.assetClass}</td>
                  <td>{gain.section}</td>
                  <td>{formatDate(gain.acquisitionDate)}</td>
                  <td>{formatDate(gain.transferDate)}</td>
                  <td>{gain.holdingPeriodDays}</td>
                  <td>₹ {formatAmount(gain.grossConsideration)}</td>
                  <td>₹ {formatAmount(gain.expenseRelatedToTransferExceptSTT)}</td>
                  <td>₹ {formatAmount(gain.actualCostOfAcquisition)}</td>
                  <td>{gain.grandfatheredFairMarketValue != null ? `₹ ${formatAmount(gain.grandfatheredFairMarketValue)}` : '-'}</td>
                  <td>₹ {formatAmount(gain.costOfAcqusition)}</td>
                  <td>{gain.costOfImprovement != null ? `₹ ${formatAmount(gain.costOfImprovement)}` : 'N/A'}</td>
                  <td>₹ {formatAmount(gain.gainAmount)}</td>
                  <td>{formatAmount(gain.applicableTaxRate)}</td>
                </tr>
              ))}
              <tr className="total-row">
                <td colSpan="11">Gross STCG</td>
                <td>₹ {stcgTotal !== null ? formatAmount(stcgTotal) : 'N/A'}</td>
                <td>-</td>
              </tr>
              <tr className="total-row">
                <td colSpan="11">Taxable STCG</td>
                <td>₹ {stcgTaxableTotal !== null ? formatAmount(stcgTaxableTotal) : 'N/A'}</td>
                <td>-</td>
              </tr>
            </tbody>
          </table>
        ) : (
          <p>No Short-Term Capital Gain data available.</p>
        )}
      </div>

      <div className="capital-gain-section">
        <h3>Duration of Short Term Capital Gains (111A)</h3>
        <table className="duration-table">
          <thead>
            <tr>
              <th>Period</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            {stcgPeriod.map((period, index) => (
              <tr key={index}>
                <td>{period.name}</td>
                <td>₹ {formatAmount(period.amount)}</td>
              </tr>
            ))}
            <tr className="total-row">
              <td>Short Term Capital Gain</td>
              <td>₹ {stcgTotal !== null ? formatAmount(stcgTotal) : '-'}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="capital-gain-section">
        <h3>Duration of Long Term Capital Gains (112A)</h3>
        <table className="duration-table">
          <thead>
            <tr>
              <th>Period</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            {ltcgPeriod.map((period, index) => (
              <tr key={index}>
                <td>{period.name}</td>
                <td>₹ {formatAmount(period.amount)}</td>
              </tr>
            ))}
            <tr className="total-row">
              <td>Long Term Capital Gain</td>
              <td>₹ {ltcgTotal !== null ? formatAmount(ltcgTotal) : '-'}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CapitalGain;
